<template>
  <Breadcrumb :items="breadcrumbs" />

  <div class="container-fluid my-3">
    <div class="section-header mb-3 d-flex justify-content-between align-items-center flex-wrap gap-2">
      <span class="section-label">الاشتراكات</span>
      <button class="btn PrimaryButton" @click="openCreate">
        <i class="pi pi-plus me-1"></i> اشتراك يدوي
      </button>
    </div>

    <!-- Filters -->
    <div class="card mb-3">
      <div class="card-body">
        <div class="row g-3 align-items-end">
          <div class="col-md-3">
            <label class="form-label">الحالة</label>
            <select class="form-select" v-model="store.filters.status" @change="reload">
              <option value="">الكل</option>
              <option v-for="(lbl, key) in statusOptions" :key="key" :value="key">{{ lbl }}</option>
            </select>
          </div>
          <div class="col-md-3">
            <label class="form-label">الخطة</label>
            <select class="form-select" v-model="store.filters.plan_type" @change="reload">
              <option value="">الكل</option>
              <option v-for="t in planTypes" :key="t" :value="t">{{ planLabel(t) }}</option>
            </select>
          </div>
          <div class="col-md-3">
            <label class="form-label">النوع</label>
            <select class="form-select" v-model="store.filters.type" @change="reload">
              <option value="">الكل</option>
              <option value="academic_year">سنة دراسية</option>
              <option value="course">كورس</option>
            </select>
          </div>
          <div class="col-md-2">
            <label class="form-label">رقم الطالب</label>
            <input type="number" class="form-control" v-model="store.filters.student_id" @keyup.enter="reload" />
          </div>
          <div class="col-md-1">
            <button class="btn btn-outline-secondary w-100" @click="resetFilters" title="إعادة تعيين">
              <i class="pi pi-refresh"></i>
            </button>
          </div>
        </div>
      </div>
    </div>

    <Loading v-if="store.loading" />

    <div v-else class="card">
      <div class="card-body p-0">
        <div v-if="!store.subscriptions.length" class="text-center py-5 text-muted">
          <i class="pi pi-inbox" style="font-size:2.5rem;"></i>
          <p class="mt-2">لا توجد اشتراكات.</p>
        </div>
        <div v-else class="table-responsive">
          <table class="table table-bordered align-middle mb-0">
            <thead class="table-light">
              <tr>
                <th>الطالب</th>
                <th>المنتج</th>
                <th>الخطة</th>
                <th>السعر</th>
                <th>الحالة</th>
                <th>يبدأ</th>
                <th>ينتهي</th>
                <th style="width:200px">إجراءات</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="s in store.subscriptions" :key="s.id">
                <td>
                  <div class="fw-bold">{{ s.student?.name || '—' }}</div>
                  <small class="text-muted">{{ s.student?.phone }}</small>
                </td>
                <td>{{ productLabel(s) }}</td>
                <td>{{ planLabel(s.plan_type) }}</td>
                <td>{{ money(s.price) }}</td>
                <td><span class="badge" :class="statusBadge(s.status)">{{ statusLabel(s.status) }}</span></td>
                <td><small>{{ date(s.starts_at) }}</small></td>
                <td><small>{{ date(s.expires_at) }}</small></td>
                <td>
                  <button v-if="s.status !== 'active'" class="btn btn-sm btn-success me-1" @click="activate(s)" title="تفعيل">
                    <i class="pi pi-check-circle"></i>
                  </button>
                  <button class="btn btn-sm btn-outline-primary me-1" @click="renew(s)" title="تجديد يدوي">
                    <i class="pi pi-refresh"></i>
                  </button>
                  <button v-if="!['cancelled','expired'].includes(s.status)" class="btn btn-sm btn-outline-danger" @click="cancel(s)" title="إلغاء">
                    <i class="pi pi-ban"></i>
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      <div class="card-footer d-flex justify-content-center" v-if="store.pagination && store.pagination.last_page > 1">
        <pagination :data="store.pagination" @pagination-change-page="store.fetchSubscriptions" class="pagination mb-0" />
      </div>
    </div>
  </div>

  <!-- Manual create dialog -->
  <Dialog v-model:visible="createOpen" modal :style="{ width: '34rem' }" :breakpoints="{ '640px': '95vw' }" header="اشتراك يدوي">
    <div class="row g-3">
      <div class="col-12">
        <label class="form-label">رقم الطالب <span class="text-danger">*</span></label>
        <input type="number" class="form-control" v-model="createForm.student_id" placeholder="ID الطالب" />
      </div>
      <div class="col-md-6">
        <label class="form-label">النوع</label>
        <select class="form-select" v-model="createForm.type" @change="createForm.subscribable_id = ''">
          <option value="academic_year">سنة دراسية</option>
          <option value="course">كورس</option>
        </select>
      </div>
      <div class="col-md-6">
        <label class="form-label">المنتج</label>
        <select class="form-select" v-model="createForm.subscribable_id">
          <option value="" disabled>اختر...</option>
          <option v-for="p in productOptions" :key="p.id" :value="p.id">{{ p.label }}</option>
        </select>
      </div>
      <div class="col-md-6">
        <label class="form-label">الخطة</label>
        <select class="form-select" v-model="createForm.plan_type">
          <option v-for="t in planTypes" :key="t" :value="t">{{ planLabel(t) }}</option>
        </select>
      </div>
      <div class="col-md-6 d-flex align-items-end gap-2">
        <InputSwitch v-model="createForm.activate" inputId="s_activate" />
        <label for="s_activate" class="mb-0">تفعيل مباشرة</label>
      </div>
      <div class="col-12">
        <label class="form-label">ملاحظات</label>
        <textarea class="form-control" rows="2" v-model="createForm.notes"></textarea>
      </div>
    </div>
    <template #footer>
      <button class="btn btn-secondary" @click="createOpen = false">إلغاء</button>
      <button class="btn PrimaryButton" :disabled="store.acting || !createForm.student_id || !createForm.subscribable_id" @click="submitCreate">
        <i class="pi pi-check me-1"></i>{{ store.acting ? 'جارٍ...' : 'إنشاء' }}
      </button>
    </template>
  </Dialog>
</template>

<script>
import axios from 'axios';
import Dialog from 'primevue/dialog';
import InputSwitch from 'primevue/inputswitch';
import pagination from 'laravel-vue-pagination';
import Swal from 'sweetalert2';
import Breadcrumb from '@/components/global/breadcrumb.vue';
import Loading from '@/components/global/loading.vue';
import { useSubscriptionStore } from '@/stores/useSubscriptionStore.js';
import { useCourseStore } from '@/stores/useCourseStore.js';
import {
  PLAN_TYPES, STATUS_LABELS, planLabel, statusLabel, statusBadge, typeLabel,
  formatMoney, formatDate, apiError,
} from '@/composables/subscriptionMeta.js';

const TYPE_FROM_MORPH = { Course: 'course', AcademicYear: 'academic_year' };

export default {
  name: 'SubscriptionsIndex',
  components: { Dialog, InputSwitch, pagination, Breadcrumb, Loading },
  data() {
    return {
      breadcrumbs: [{ name: 'الرئيسية', path: '/' }, { name: 'الاشتراكات', path: '/subscriptions' }],
      planTypes: PLAN_TYPES,
      statusOptions: STATUS_LABELS,
      createOpen: false,
      createForm: { student_id: '', type: 'course', subscribable_id: '', plan_type: 'monthly', activate: false, notes: '' },
      academicYears: [],
    };
  },
  computed: {
    store() { return useSubscriptionStore(); },
    courseStore() { return useCourseStore(); },
    productOptions() {
      if (this.createForm.type === 'course') {
        return this.courseStore.courses.map(c => ({ id: c.id, label: c.title }));
      }
      return this.academicYears.map(y => ({ id: y.id, label: y.name }));
    },
  },
  methods: {
    planLabel, statusLabel, statusBadge,
    money: formatMoney,
    date: formatDate,
    productLabel(s) {
      const p = s.subscribable;
      const name = p?.name || p?.title || '';
      const type = typeLabel(s.subscribable_type);
      return name ? `${name} (${type})` : type || '—';
    },
    reload() { this.store.fetchSubscriptions(1); },
    resetFilters() {
      Object.assign(this.store.filters, { status: '', plan_type: '', type: '', student_id: '' });
      this.store.fetchSubscriptions(1);
    },
    async activate(s) {
      const res = await Swal.fire({
        title: 'تفعيل الاشتراك', text: 'سيتم احتساب تاريخ الانتهاء تلقائياً حسب الخطة.',
        icon: 'question', showCancelButton: true, confirmButtonText: 'تفعيل', cancelButtonText: 'إلغاء', confirmButtonColor: '#16a34a',
      });
      if (!res.isConfirmed) return;
      try {
        await this.store.activate(s.id);
        await this.store.fetchSubscriptions(this.store.pagination?.current_page || 1);
        Swal.fire({ icon: 'success', title: 'تم التفعيل', timer: 1200, showConfirmButton: false });
      } catch (e) { Swal.fire({ icon: 'error', title: apiError(e), timer: 2500, showConfirmButton: false }); }
    },
    async cancel(s) {
      const res = await Swal.fire({
        title: 'إلغاء الاشتراك', icon: 'warning', showCancelButton: true,
        confirmButtonText: 'نعم، إلغاء', cancelButtonText: 'رجوع', confirmButtonColor: '#dc3545',
      });
      if (!res.isConfirmed) return;
      try {
        await this.store.cancel(s.id);
        await this.store.fetchSubscriptions(this.store.pagination?.current_page || 1);
        Swal.fire({ icon: 'success', title: 'تم الإلغاء', timer: 1200, showConfirmButton: false });
      } catch (e) { Swal.fire({ icon: 'error', title: apiError(e), timer: 2500, showConfirmButton: false }); }
    },
    async renew(s) {
      const type = TYPE_FROM_MORPH[String(s.subscribable_type).split('\\').pop()] || 'course';
      const res = await Swal.fire({
        title: 'تجديد يدوي', text: 'سيتم إنشاء اشتراك جديد بنفس المنتج والخطة وتفعيله.',
        icon: 'question', showCancelButton: true, confirmButtonText: 'تجديد وتفعيل', cancelButtonText: 'إلغاء',
      });
      if (!res.isConfirmed) return;
      try {
        await this.store.create({
          student_id: s.student_id, type, subscribable_id: s.subscribable_id,
          plan_type: s.plan_type, activate: true,
        });
        await this.store.fetchSubscriptions(this.store.pagination?.current_page || 1);
        Swal.fire({ icon: 'success', title: 'تم التجديد', timer: 1200, showConfirmButton: false });
      } catch (e) { Swal.fire({ icon: 'error', title: apiError(e), timer: 2500, showConfirmButton: false }); }
    },
    async openCreate() {
      this.createForm = { student_id: '', type: 'course', subscribable_id: '', plan_type: 'monthly', activate: false, notes: '' };
      this.createOpen = true;
      if (!this.courseStore.courses.length) this.courseStore.fetchCourses();
      if (!this.academicYears.length) {
        try { const { data } = await axios.get('/academic-years'); this.academicYears = data.data ?? []; } catch { /* non-fatal */ }
      }
    },
    async submitCreate() {
      try {
        await this.store.create({
          student_id: this.createForm.student_id,
          type: this.createForm.type,
          subscribable_id: this.createForm.subscribable_id,
          plan_type: this.createForm.plan_type,
          notes: this.createForm.notes || null,
          activate: this.createForm.activate,
        });
        this.createOpen = false;
        await this.store.fetchSubscriptions(1);
        Swal.fire({ icon: 'success', title: 'تم إنشاء الاشتراك', timer: 1200, showConfirmButton: false });
      } catch (e) { Swal.fire({ icon: 'error', title: apiError(e), timer: 2500, showConfirmButton: false }); }
    },
  },
  mounted() {
    this.store.fetchSubscriptions(1);
  },
};
</script>
